import { Badge, BlockStack, Box, InlineStack, Typography, type BadgeTone } from '@pv-frontend/ui-components-web';
import { formatUkAndUtc } from './format-date';
import {
    preferenceSourceLabel,
    preferenceStatusLabel,
    type HomeFeedPreferencesView,
    type PreferenceItemView,
    type PreferenceOpenPointView,
} from './home-feed-view';

export interface HomeFeedPreferencesProps {
    preferences: HomeFeedPreferencesView;
    /** A member's email when known (vault member list), else their account id. */
    memberLabel: (accountId: string) => string;
}

const STATUS_TONES: Record<string, BadgeTone> = {
    APPLIED: 'success',
    PENDING: 'warning',
    DECLINED: 'neutral',
};

function joinNames(names: readonly string[]): string {
    return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function OpenPoint({
    point,
    memberLabel,
    testID,
    lead,
}: {
    point: PreferenceOpenPointView;
    memberLabel: (accountId: string) => string;
    testID: string;
    lead: string;
}) {
    return (
        <Box testID={testID}>
        <BlockStack gap="050">
            <Typography variant="body-sm" fontWeight="medium">
                {`${point.subject} (${point.kind}): ${lead}`}
            </Typography>
            <Typography variant="body-sm" tone="text-secondary">
                {point.householdValue === null
                    ? 'The household holds nothing for this yet.'
                    : `The household holds ${point.householdValue} now.`}
            </Typography>
            {point.views.map((view) => (
                <Typography key={view.memberAccountId} variant="body-sm" tone="text-secondary">
                    {`${memberLabel(view.memberAccountId)}: ${view.value} · ${preferenceStatusLabel(view.householdStatus)}` +
                        (view.overriddenByAccountId
                            ? ` · overridden by ${memberLabel(view.overriddenByAccountId)}`
                            : '')}
                </Typography>
            ))}
        </BlockStack>
    </Box>
    );
}

function Item({ item, memberLabel }: { item: PreferenceItemView; memberLabel: (id: string) => string }) {
    const when = formatUkAndUtc(item.statedAt);
    return (
        <Box testID="home-feed-preference">
        <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center" wrap>
                <Typography variant="body-md" fontWeight="medium">
                    {`${item.subject}: ${item.value}`}
                </Typography>
                <Badge tone={STATUS_TONES[item.householdStatus] ?? 'neutral'}>
                    {preferenceStatusLabel(item.householdStatus)}
                </Badge>
            </InlineStack>
            <Typography variant="body-sm" tone="text-secondary">
                {`${item.kind} · ${preferenceSourceLabel(item.source)} · stated ${when.uk} (${when.utc})`}
            </Typography>
            {item.overriddenByAccountId ? (
                <Typography variant="body-sm" tone="text-secondary">
                    {`Overridden by ${memberLabel(item.overriddenByAccountId)}`}
                </Typography>
            ) : null}
        </BlockStack>
    </Box>
    );
}

/**
 * The household summary line and the member's preferences (PRD pv-platform#156). Read-only. The summary
 * is the household's stored status, not re-decided here: `held` is Applied on one value (a lone statement
 * is "held by"; two or more members each stated or "me too"'d it, so "agreed by"), `pending` waits on a partner, `conflicts` is Declined or differing values.
 */
export function HomeFeedPreferences({ preferences, memberLabel }: HomeFeedPreferencesProps) {
    const { held, pending, conflicts, items } = preferences;
    const nothingStated = held.length + pending.length + conflicts.length === 0;
    return (
        <BlockStack gap="300">
            <Box testID="home-feed-household-summary">
            <BlockStack gap="100">
                <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                    Household
                </Typography>
                {nothingStated ? (
                    <Typography variant="body-md" tone="text-secondary">
                        No preferences stated in this household yet.
                    </Typography>
                ) : null}
                {held.map((line) => (
                    <Typography key={`held:${line.kind}:${line.subject}`} variant="body-md" testID="home-feed-held">
                        {`${line.subject} (${line.kind}): ${line.value}, ${
                            line.memberAccountIds.length > 1 ? 'agreed' : 'held'
                        } by ${joinNames(line.memberAccountIds.map(memberLabel))}`}
                    </Typography>
                ))}
                {pending.map((point) => (
                    <OpenPoint
                        key={`pending:${point.kind}:${point.subject}`}
                        point={point}
                        memberLabel={memberLabel}
                        testID="home-feed-pending"
                        lead="awaiting a partner's answer"
                    />
                ))}
                {conflicts.map((point) => (
                    <OpenPoint
                        key={`conflict:${point.kind}:${point.subject}`}
                        point={point}
                        memberLabel={memberLabel}
                        testID="home-feed-conflict"
                        lead="conflict"
                    />
                ))}
            </BlockStack>
        </Box>

            <BlockStack gap="200">
                <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                    Preferences
                </Typography>
                {items.length === 0 ? (
                    <Typography variant="body-md" tone="text-secondary" testID="home-feed-no-preferences">
                        This member has stated no preferences on this Search.
                    </Typography>
                ) : (
                    items.map((item) => (
                        <Item key={`${item.kind}:${item.subject}`} item={item} memberLabel={memberLabel} />
                    ))
                )}
            </BlockStack>
        </BlockStack>
    );
}
